'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

const navLinks = [
  { label: 'Home', href: '/homepage' },
  {
    label: 'Products',
    href: '/products',
    dropdown: [
      { label: 'Ride Hailing Platform', href: '/products/ride-hailing', icon: 'TruckIcon' },
      { label: 'Logistics Platform', href: '/products/logistics', icon: 'ArrowPathIcon' },
      { label: 'Facility Pro', href: '/products/facility-pro', icon: 'HomeModernIcon' },
      { label: 'Delivery Services', href: '/products/courier', icon: 'BoltIcon' },
      { label: 'AI Based Call Center', href: '/products/ai-call-center', icon: 'PhoneIcon' },
      { label: 'E-Commerce & Retail', href: '/products/ecommerce', icon: 'ShoppingBagIcon' },
      { label: 'Ellan CPaaS', href: '/products/ellan-cpaas', icon: 'ChatBubbleLeftRightIcon' },
    ],
  },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    setMenuOpen(false);
    setActiveDropdown(null);
  }, [pathname]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-[rgba(0,102,255,0.1)] shadow-[0_2px_20px_-4px_rgba(15,23,42,0.08)]"
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link
              href="/homepage"
              className="flex items-center transition-all duration-300"
              aria-label="Movio - Home"
            >
              <Image
                src="/assets/images/movio-logo.png"
                alt="Movio"
                width={150}
                height={46}
                className="object-contain"
                priority
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1" role="navigation" aria-label="Main navigation">
              {navLinks?.map((link) => (
                <div
                  key={link?.href + link?.label}
                  className="relative"
                  onMouseEnter={() => link.dropdown ? setActiveDropdown(link.label) : null}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    href={link?.href}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover-underline flex items-center gap-1 ${pathname === link?.href || pathname?.startsWith(link?.href + '/')
                     ? 'text-[#0066FF] font-semibold'
: 'text-[#475569] hover:text-[#0066FF]'
                      }`}
                    aria-current={pathname === link?.href ? 'page' : undefined}
                  >
                    {link?.label}
                    {link.dropdown && (
                      <Icon name="ChevronDownIcon" size={14} className={`transition-transform duration-200 ${activeDropdown === link.label ? 'rotate-180' : ''}`} />
                    )}
                  </Link>
                  {/* Dropdown */}
                  {link.dropdown && activeDropdown === link.label && (
                    <div className="absolute top-full left-0 mt-1 w-[480px] bg-white rounded-2xl shadow-2xl border border-[rgba(15,23,42,0.08)] p-3 z-50">
                      <div className="grid grid-cols-2 gap-1">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.label}
                            href={item.href}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#475569] hover:text-[#0066FF] hover:bg-[rgba(0,102,255,0.05)] transition-colors group"
                          >
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(0,102,255,0.07)] group-hover:bg-[rgba(0,102,255,0.12)] transition-colors">
                              <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-[#0066FF]" />
                            </div>
                            <span className="font-medium leading-tight">{item.label}</span>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-[rgba(15,23,42,0.06)] px-1">
                        <Link
                          href="/products"
                          className="flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold text-[#0066FF] hover:bg-[rgba(0,102,255,0.05)] transition-colors"
                        >
                          View all 7 products
                          <Icon name="ArrowRightIcon" size={13} />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                href="/dashboard-preview"
                className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-[#0066FF] border border-[rgba(0,102,255,0.3)] hover:bg-[rgba(0,102,255,0.06)]"
                aria-label="View dashboard preview"
              >
                Dashboard
              </Link>
<Link
  href="/demo-booking"
  className="px-5 py-2.5 text-sm font-semibold rounded-lg
  bg-gradient-to-r from-[#3B82F6] to-[#2563EB]
  text-white transition-all duration-300 ease-out
  hover:from-[#2563EB] hover:to-[#1D4ED8]
  hover:shadow-xl
  hover:-translate-y-0.5"
>
  Book Demo
</Link>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors text-[#0066FF] hover:bg-[#FFFFFF]"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              <Icon name={menuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        role="dialog"
        aria-label="Mobile navigation menu"
        aria-modal="true"
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-2xl md:hidden transform transition-transform duration-300 ${menuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#FFFFFF]">
          <div className="flex items-center">
            <Image
              src="/assets/images/movio-logo.png"
              alt="Movio"
              width={110}
              height={34}
              className="object-contain rounded-md"
              priority
            />
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-[#0066FF] hover:bg-[#FFFFFF]"
            aria-label="Close menu"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <nav className="p-4 flex flex-col gap-1" aria-label="Mobile navigation">
          {navLinks?.map((link) => (
            <Link
              key={link?.href + link?.label}
              href={link?.href}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${pathname === link?.href
                  ? 'bg-[rgba(0,102,255,0.08)] text-[#0066FF] font-semibold'
                  : 'text-[#0066FF]/80 hover:bg-[#FFFFFF] hover:text-[#0066FF]'
                }`}
              aria-current={pathname === link?.href ? 'page' : undefined}
            >
              {link?.label}
            </Link>
          ))}
          <div className="mt-4 pt-4 border-t border-[#FFFFFF] flex flex-col gap-2">
            <Link
              href="/dashboard-preview"
              className="px-4 py-3 rounded-xl text-sm font-semibold text-center text-[#0066FF] border border-[rgba(0,102,255,0.3)] hover:bg-[rgba(0,102,255,0.06)]"
            >
              Dashboard Preview
            </Link>
        <Link
  href="/demo-booking"
  className="px-4 py-3 rounded-xl text-sm font-semibold text-center
  bg-[#3B82F6] text-white
  transition-all duration-300 ease-out
  hover:bg-[#2563EB]
  hover:shadow-lg
  hover:-translate-y-0.5"
>
  Book Demo
</Link>
          </div>
        </nav>
      </div>
    </>
  );
}